import type { FC } from 'react'
import { useNavigate, useMatch } from 'react-router-dom'
import * as Yup from 'yup'
import { ErrorMessage, Form, Formik } from 'formik'
import { useTranslation } from 'react-i18next'

import { Box, Button, FormControlLabel, Grid, CircularProgress, Radio, type Theme, type SxProps } from '@dhl/theme'
import { useMopinion as useFeedback } from '@dhl/feedback'
import {
  CheckboxInput,
  createEmailValidationSchema,
  createFirstNameValidationSchema,
  createLastNameValidationSchema,
  EmailInput,
  FirstNameInput,
  LastNameInput,
  RadioGroupInput,
} from '@dhl/inputs'
import { Contents } from '@dhl-ui/prismic'
import { FormError } from '@dhl/error'
import { useSnackbar } from '@dhl-ui/components'
import { NativeModal, type NativeModalProps } from '@dhl/native-modal'

import { usePromotion, useMopinionSurvey } from '~/hooks/index.js'
import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { getConfig } from '~/config/index.js'

const classes: Record<string, SxProps<Theme>> = {
  tocRoot: {
    margin: 0,
    alignItems: 'flex-start',
    '& .MuiCheckbox-root': {
      marginLeft: 2,
      marginTop: -8,
    },
  },
}

type FormValues = {
  firstName: string
  lastName: string
  email: string
  conditions: boolean
  userId: string
  quiz: string
}

type MopinionFormValue = {
  type: string
  value: string
  label: string
}

type PromotionsDialogProps = Omit<NativeModalProps, 'open' | 'onClose'>

const Spinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
    <CircularProgress />
  </Box>
)

const sendFieldMapper: Record<string, keyof FormValues> = {
  firstname: 'firstName',
  lastname: 'lastName',
  email: 'email',
  CHECKBOX1: 'conditions',
  INPUT1: 'userId',
  radio: 'quiz',
}

export const PromotionDialog: FC<PromotionsDialogProps> = (promotionDialogProps) => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const lang = i18n.language.toLowerCase().replace('_', '-')
  const match = useMatch('/:originalPath/promotions/:promotionId')
  const { promotionId, originalPath } = match?.params || {}

  const { data: promotion, isLoading: isLoadingPromotion } = usePromotion(promotionId ?? '', lang)
  const { data: mopinionSurvey, isLoading: isLoadingMopinion } = useMopinionSurvey('feedback', promotion?.surveyKey)

  const { enqueueSnackbar } = useSnackbar()
  const { data: profile } = useGetProfile()

  const {
    mopinionSurveys: {
      feedback: { token: mopinionToken },
    },
    mopinion: { token: mopinionFallbackToken }, // Clean up mopinionFallbackToken after mopinionSurveys is fully rolled out
  } = getConfig()
  const { enabled: feedbackEnabled, reportFeedback } = useFeedback(mopinionToken || mopinionFallbackToken)

  if (!feedbackEnabled) {
    return null
  }

  const onClose = () => navigate(originalPath ? `/${originalPath}` : '/dashboard')

  const initialValues: FormValues = {
    firstName: profile ? profile.firstName : '',
    lastName: profile ? profile.lastName : '',
    email: profile ? profile.email : '',
    conditions: false,
    userId: profile ? profile.userId : '',
    quiz: '',
  }

  const validationSchema: Yup.Schema<FormValues> = Yup.object({
    firstName: createFirstNameValidationSchema(t),
    lastName: createLastNameValidationSchema(t),
    email: createEmailValidationSchema(t),
    conditions: Yup.bool().default(false).oneOf([true], t('portal.promotions.agree_conditions')).required(),
    userId: Yup.string().default(''),
    quiz: Yup.string().required(t('portal.promotions.quizRequiredText')),
  })

  const onSubmit = async (values: FormValues): Promise<void> => {
    const mopinionFormValues: MopinionFormValue[] =
      mopinionSurvey?.sendOptions?.data.map((sendOption) => {
        const [, sendOptionName] = sendOption.label.split('.')
        return {
          label: sendOption.label,
          value: `${values[sendFieldMapper[sendOptionName]]}`,
          type: sendOption.type,
        }
      }) || []

    mopinionFormValues.push({
      label: 'Survey',
      value: `${mopinionSurvey?.properties.name}`,
      type: 'category',
    })

    try {
      await reportFeedback(Number(mopinionSurvey?.properties.id), mopinionFormValues)
      enqueueSnackbar(t('portal.promotions.successText'), {
        severity: 'success',
      })
    } catch {
      enqueueSnackbar(t('common.pleaseTryAgainErrorMessage'), { severity: 'error' })
    } finally {
      onClose()
    }
  }

  const isLoading = isLoadingPromotion || isLoadingMopinion || promotion?.id !== promotionId
  const hasData = !isLoading && promotion && mopinionSurvey

  return (
    <NativeModal open={!!match} {...promotionDialogProps} onClose={onClose}>
      <NativeModal.Header title={!isLoading ? promotion?.title : ''} onClose={onClose} />
      <NativeModal.Content>
        <>
          {isLoading && <Spinner />}
          {hasData && (
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              validateOnBlur
              validateOnChange
              enableReinitialize
            >
              <Form noValidate>
                <Grid container direction="column" spacing={2}>
                  <Grid size={12} sx={{ width: '100%', paddingTop: 0 }}>
                    <img src={promotion.image?.url} alt={promotion.title} style={{ width: '100%' }} />
                  </Grid>
                  <Grid size={12} style={{ paddingTop: 0 }}>
                    <Contents field={promotion.description} />
                  </Grid>
                  <Grid>
                    <RadioGroupInput name="quiz">
                      <Grid container wrap="nowrap" direction="column">
                        <Grid>
                          <FormControlLabel
                            value="Max Verstappen"
                            control={<Radio />}
                            label="Max Verstappen"
                            labelPlacement="end"
                          />
                        </Grid>
                        <Grid>
                          <FormControlLabel
                            value="Lewis Hamilton"
                            control={<Radio />}
                            label="Lewis Hamilton"
                            labelPlacement="end"
                          />
                        </Grid>
                        <Grid>
                          <FormControlLabel
                            value="Charles Leclerc"
                            control={<Radio />}
                            label="Charles Leclerc"
                            labelPlacement="end"
                          />
                        </Grid>
                      </Grid>
                    </RadioGroupInput>
                    <ErrorMessage name="quiz" component={FormError} />
                  </Grid>
                  <Grid>
                    <FirstNameInput name="firstName" fullWidth />
                  </Grid>
                  <Grid>
                    <LastNameInput name="lastName" fullWidth />
                  </Grid>
                  <Grid>
                    <EmailInput name="email" fullWidth />
                  </Grid>
                  <Grid size={12}>
                    <FormControlLabel
                      sx={classes.tocRoot}
                      data-test="checkbox-toc"
                      control={<CheckboxInput name="conditions" />}
                      label={<Contents field={promotion?.optIn} />}
                    />
                    <ErrorMessage name="conditions" component={FormError} />
                  </Grid>
                  <Grid>
                    <Button variant="contained" color="primary" type="submit" fullWidth>
                      {t('feedback.feedbackDialog.sendButton')}
                    </Button>
                  </Grid>
                </Grid>
              </Form>
            </Formik>
          )}
        </>
      </NativeModal.Content>
    </NativeModal>
  )
}
